import type { Dayjs } from 'dayjs'
import { z } from 'zod'

import { dateUtil, isNull } from '../../util'
import { csvColumnLabelFor } from '../csvColumnLabel'

import { normalizeReferenceNumber } from './normalizeReferenceNumber'
import { normalizeScrmSiteCode } from './normalizeScrmSiteCode'

//
// SCRM データ CSV（確定フォーマット 20 列・Shift_JIS）の行バリデーション・正規化。
// 仕様: docs/domain/order_matching/05_csv_specs.md §2（確定サンプル
// `SCRMデータサンプル_20260727確定20列.csv` + SCRM 項目定義書の現物で確定・2026/07/29）
// - 必須は購買ｵｰﾀﾞ番号・ｻｲﾄのみ。`**`（本体工事）・`*`（工区分けなし）・
//   空欄（在庫用発注）は欠損ではなく意味のある値のため弾かない（A-5 解決）。
//   他の列は空欄を許容して null に寄せる（A-9 は確認中）
// - ｻｲﾄ列は行単位のテナント振り分けキー。`07` のようなゼロ埋め表記を正規化する
// - 発注形態列（2026/07/27 追加・A-4 解決）はロール品行の判別キー。
//   旧 19 列 CSV（列自体が無い）も取り込めるよう任意列とする
//

export const scrmCsvColumnMapper = {
  orderNumber: '購買ｵｰﾀﾞ番号',
  siteCode: 'ｻｲﾄ',
  siteName: 'ｻｲﾄ名称', // raw にのみ保持
  supplierName: '仕入先名',
  itemNumber: '品目番号',
  symbol: '記号',
  quantity: '発注数',
  length: '長さ(mm)',
  material: '材質',
  weight: '数量(重量)',
  category3: '区分3', // raw にのみ保持
  category3Name: '区分3名称',
  constructionNote: '建築備考',
  constructionNumber: '工事番号',
  managementNumber: '管理番号',
  constructionArea: '工区', // raw にのみ保持
  constructionName: '工事名称',
  orderCategory: '発注形態',
  answeredDeliveryDate: '回答納入日',
  acceptanceDate: '最終検収日',
}

// 発注形態のロール品判定。CSV の実値は半角カナ（`ﾛｰﾙ`）だが、項目定義書の表記は
// 全角（ロール）のため、判定は全角・半角の両表記を許容する（05_csv_specs.md §2）。
// export しているのは DB 側の絞り込み（ScrmRecord.orderCategory は取込時に trim 済みのため
// in 条件で使える）にも同じ値を使うため（recomputeSteelOrderStatusesService）
export const ROLL_ORDER_CATEGORY_VALUES: readonly string[] = ['ﾛｰﾙ', 'ロール']

export function isRollScrmOrderCategory(orderCategory: string | null): boolean {
  if (isNull(orderCategory)) {
    return false
  }
  return ROLL_ORDER_CATEGORY_VALUES.includes(orderCategory.trim())
}

// 空欄を null に寄せる任意入力の文字列列
const optionalStringColumn = z
  .string()
  .optional()
  .transform((x) => {
    if (isNull(x)) {
      return null
    }
    const trimmed = x.trim()
    return trimmed === '' ? null : trimmed
  })

// 空欄を null に寄せる任意入力の整数列（カンマ区切りは 3 桁区切りのみ許容。
// `1,2,3` や `,,` のような不正値は重量照合の入力を壊すため弾く）
const optionalIntegerColumn = z.preprocess(
  (v) => (typeof v === 'number' ? String(v) : (v ?? '')),
  z
    .string()
    .transform((x, ctx) => {
      const trimmed = x.trim()
      if (trimmed === '') {
        return null
      }
      if (!/^\d+$/.test(trimmed) && !/^\d{1,3}(,\d{3})+$/.test(trimmed)) {
        ctx.addIssue({ code: 'custom', message: '整数で入力してください。' })
        return z.NEVER
      }
      return Number(trimmed.replace(/,/g, ''))
    })
    .nullable(),
)

export function validateAndNormalizeScrmCsvRecords({
  record,
  index,
}: {
  record: Record<string, string | number | null>
  index: number
}) {
  // 全列が空の行はスキップ（デリバリー管理表と同じ扱い。防御的に入れておく）
  if (Object.values(record).every((x) => x === '' || isNull(x))) {
    return {
      success: false as const,
      isEmptyLine: true as const,
      message: `空行です。`,
      record,
      index,
    }
  }

  const data = {
    orderNumber: record[scrmCsvColumnMapper.orderNumber],
    siteCode: record[scrmCsvColumnMapper.siteCode],
    supplierName: record[scrmCsvColumnMapper.supplierName],
    itemNumber: record[scrmCsvColumnMapper.itemNumber],
    symbol: record[scrmCsvColumnMapper.symbol],
    quantity: record[scrmCsvColumnMapper.quantity],
    length: record[scrmCsvColumnMapper.length],
    material: record[scrmCsvColumnMapper.material],
    weight: record[scrmCsvColumnMapper.weight],
    category3Name: record[scrmCsvColumnMapper.category3Name],
    constructionNote: record[scrmCsvColumnMapper.constructionNote],
    constructionNumber: record[scrmCsvColumnMapper.constructionNumber],
    managementNumber: record[scrmCsvColumnMapper.managementNumber],
    constructionName: record[scrmCsvColumnMapper.constructionName],
    orderCategory: record[scrmCsvColumnMapper.orderCategory],
    answeredDeliveryDate: record[scrmCsvColumnMapper.answeredDeliveryDate],
    acceptanceDate: record[scrmCsvColumnMapper.acceptanceDate],
  }

  const schema = z.object({
    // 購買オーダ番号はスポット品の発注（親）のキー。契約番号と同じ正規化を通した値で保存する。
    // csv-parse の設定次第で数値として渡りうるため、ｻｲﾄ列と同様に文字列へ寄せてから検証する
    orderNumber: z.preprocess(
      (v) => (typeof v === 'number' ? String(v) : (v ?? '')),
      z
        .string()
        .transform((x) => normalizeReferenceNumber(x))
        .pipe(z.string().min(1, { message: '購買ｵｰﾀﾞ番号は必須です。' })),
    ),
    // ｻｲﾄはテナント振り分けキーのため必須。ゼロ埋め差（`07` / `7`）を正規化する
    siteCode: z.preprocess(
      (v) => (typeof v === 'number' ? String(v) : (v ?? '')),
      z
        .string()
        .transform((x) => normalizeScrmSiteCode(x))
        .pipe(z.string().min(1, { message: 'ｻｲﾄは必須です。' })),
    ),
    supplierName: optionalStringColumn,
    itemNumber: optionalStringColumn,
    symbol: optionalStringColumn,
    quantity: optionalIntegerColumn,
    length: optionalStringColumn,
    material: optionalStringColumn,
    weight: optionalIntegerColumn,
    category3Name: optionalStringColumn,
    constructionNote: optionalStringColumn,
    constructionNumber: optionalStringColumn,
    managementNumber: optionalStringColumn,
    constructionName: optionalStringColumn,
    // 空欄 = 「1 式」発注の判別キーのため、空欄と値ありを区別して保持する。
    // 値の検証はしない（今後の値追加に頑健にする。ロール品判定は isRollScrmOrderCategory）
    orderCategory: optionalStringColumn,
    answeredDeliveryDate: optionalStringColumn,
    acceptanceDate: optionalStringColumn,
  })
  const parsed = schema.safeParse(data)

  if (!parsed.success) {
    return {
      success: false as const,
      message: parsed.error.issues
        .map((x) => {
          const label = csvColumnLabelFor(scrmCsvColumnMapper, x.path.join(''))
          const value = data[x.path.join('') as keyof typeof data]
          return `${label}: ${x.message}(入力された値: ${value})`
        })
        .join('。'),
      record,
      index,
    }
  }

  // 日付列は値がある場合のみ形式チェック（実サンプルは `2026/7/6` の YYYY/M/D 形式）
  const parseOptionalDate = (
    label: string,
    value: string | null,
  ): { success: true; date: Dayjs | null } | { success: false; message: string } => {
    if (isNull(value)) {
      return { success: true, date: null }
    }
    const dateParsed = dateUtil.parseDateFormatString(value)
    if (isNull(dateParsed) || dateParsed.timeframe !== 'day') {
      return { success: false, message: `${label}: 日付形式がYYYY/MM/DDの形式になっていません。入力：${value}` }
    }
    return { success: true, date: dateParsed.date }
  }

  const answeredDeliveryDate = parseOptionalDate(
    scrmCsvColumnMapper.answeredDeliveryDate,
    parsed.data.answeredDeliveryDate,
  )
  if (!answeredDeliveryDate.success) {
    return { success: false as const, message: answeredDeliveryDate.message, record, index }
  }
  const acceptanceDate = parseOptionalDate(scrmCsvColumnMapper.acceptanceDate, parsed.data.acceptanceDate)
  if (!acceptanceDate.success) {
    return { success: false as const, message: acceptanceDate.message, record, index }
  }

  const result = {
    ...parsed.data,
    // 明細（SteelOrderItem.answeredDeliveryDate）は表示用に原文の文字列を保持し、
    // 生データ（ScrmRecord.answeredDeliveryDate）は日付型で保存するため両方の形を返す
    answeredDeliveryDateText: parsed.data.answeredDeliveryDate,
    answeredDeliveryDate: answeredDeliveryDate.date,
    acceptanceDate: acceptanceDate.date,
    rank: index + 1,
    raw: record,
  }

  return {
    success: true as const,
    record,
    index,
    result,
    resultTitle: result.orderNumber,
  }
}
